import { Fragment } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "../rye/RyeIcon";
import { MockAppFrame, MockPanel } from "./MockAppFrame";
import { BRAND_MARKS } from "./MerchantBrandMarks";
import bofaLogo from "../images/bofa_logo.png";
import venmoLogo from "../images/venmo_logo.png";
import vanguardLogo from "../images/vanguard_logo.png";

// A hand-built facsimile of the transactions screen, for the marketing page.
//
// Deliberately NOT the real components: TransactionRow depends on four contexts
// and ~14 column components, so reusing it would drag the app bundle onto the
// landing page. The tradeoff is drift -- when the real screen changes, this has
// to be updated by hand.
//
// Metrics below are copied from the real implementation. If you change the app,
// change these to match:
//   top bar           h-11 row: title, then search, then actions on the right
//                     (RyeMainContentHeader / RyeMainContentHeaderRow)
//   side padding      px-[18px] (STANDARD_MAIN_CONTENT_CARD_PADDING_X), with
//                     the table and its headers pulled out by -mx-2
//                     (TRANSACTIONS_TABLE_INSET)
//   header row        h-10, py-1, font-medium, right under the top bar with no
//                     line beneath (TransactionsColumnHeaders / TransactionsHeader)
//   row               h-10, py-1, border-t-tiny border-t-surface-600 (TransactionRow)
//   cell padding      px-3            (TRANSACTION_CELL_PADDING_X)
//   column spacer     w-2 max-w-5 grow-[0.1] shrink-[4] (TransactionColumnSpacer)
//   column widths     see COLUMN_* below (TransactionColumnSizer)
//   footer            py-2 badges at the right (RyeMainContentFooter)
//   base text size    text-md via index.css on div/span/button/input
//
// All data here is fabricated. Never paste real transactions into this file.

type MockRow = {
  date: string;
  account: string;
  institution: string;
  merchant: string | null;
  description: string;
  category: { emoji: string; name: string } | null;
  tag: string | null;
  amount: string;
  positive?: boolean;
  hasRule?: boolean;
  // Independent of `category`: a rule can assign a category on import while the
  // transaction still sits in the review queue. Defaults to reviewed.
  reviewed?: boolean;
};

const ROWS: MockRow[] = [
  {
    date: "Aug 23, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Netflix",
    description: "NETFLIX.COM 8665797",
    category: { emoji: "📺", name: "Digital Subscriptions" },
    tag: null,
    amount: "−$13.99",
    hasRule: true,
    reviewed: false,
  },
  {
    date: "Aug 22, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Target",
    description: "TARGET T-1842",
    category: { emoji: "🥑", name: "Groceries" },
    tag: null,
    amount: "−$88.41",
    hasRule: true,
    reviewed: false,
  },
  {
    // The uncategorized row -- the one the animation will fill in. Matches the
    // real app, where an uncategorized cell renders empty until hover/selection.
    date: "Aug 21, 2026",
    account: "Venmo",
    institution: "Venmo",
    merchant: null,
    description: "Ice cream 🍦",
    category: null,
    tag: null,
    amount: "−$12.00",
    reviewed: false,
  },
  {
    date: "Aug 21, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Etsy",
    description: "ETSY.COM*A9F3B2K48",
    category: { emoji: "🎁", name: "Gifts" },
    tag: null,
    amount: "−$33.01",
  },
  {
    date: "Aug 19, 2026",
    account: "Venmo",
    institution: "Venmo",
    merchant: null,
    description: "Concert tickets 🎟",
    category: { emoji: "🎉", name: "Fun" },
    tag: null,
    amount: "−$68.00",
  },
  {
    date: "Aug 16, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "The North Face",
    description: "THE NORTH FACE 0182",
    category: { emoji: "🧥", name: "Clothing" },
    tag: null,
    amount: "−$109.20",
  },
  {
    date: "Aug 15, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Southwest",
    description: "SOUTHWEST AIR 5262",
    category: { emoji: "🌴", name: "Trips" },
    tag: "Hawaii 2026",
    amount: "−$412.60",
  },
  {
    date: "Aug 14, 2026",
    account: "BofA Checking",
    institution: "Bank of America",
    merchant: null,
    description: "PAYROLL DES:DIR DEP",
    category: { emoji: "💰", name: "Paycheck" },
    tag: null,
    amount: "+$3,120.55",
    positive: true,
    hasRule: true,
  },
  {
    date: "Aug 12, 2026",
    account: "BofA Checking",
    institution: "Bank of America",
    merchant: null,
    description: "CREDIT CRD AUTOPAY",
    category: { emoji: "🔄", name: "Internal Transfers" },
    tag: null,
    amount: "−$1,359.79",
    hasRule: true,
  },
  {
    date: "Aug 11, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "DoorDash",
    description: "DOORDASH*CHIPOTLE",
    category: { emoji: "🌮", name: "Restaurants" },
    tag: null,
    amount: "−$14.85",
  },
  {
    date: "Aug 10, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Shell",
    description: "SHELL OIL 574412",
    category: { emoji: "⛽", name: "Gas" },
    tag: null,
    amount: "−$48.22",
  },
  {
    date: "Aug 9, 2026",
    account: "Vanguard 401k",
    institution: "Vanguard",
    merchant: null,
    description: "VANGUARD BUY VFIAX",
    category: { emoji: "📈", name: "401k Savings" },
    tag: null,
    amount: "+$650.00",
    positive: true,
    hasRule: true,
  },
  {
    date: "Aug 8, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Uber",
    description: "UBER *TRIP 8H2K1",
    category: { emoji: "🚗", name: "Transport" },
    tag: null,
    amount: "−$62.14",
    hasRule: true,
  },
  {
    date: "Aug 7, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Spotify",
    description: "SPOTIFY P0A1B2C3",
    category: { emoji: "📺", name: "Digital Subscriptions" },
    tag: null,
    amount: "−$11.99",
    hasRule: true,
  },
  {
    date: "Aug 6, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: null,
    description: "SQ *BLUE MOON CAFE",
    category: { emoji: "☕", name: "Coffee" },
    tag: null,
    amount: "−$8.75",
  },
  {
    date: "Aug 5, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: null,
    description: "SQ *IDEAL MARKET",
    category: { emoji: "🥑", name: "Groceries" },
    tag: null,
    amount: "−$41.32",
  },
  {
    date: "Aug 4, 2026",
    account: "BofA Checking",
    institution: "Bank of America",
    merchant: null,
    description: "ACH DEBIT STATE FARM",
    category: { emoji: "🚗", name: "Insurance" },
    tag: null,
    amount: "−$128.40",
    hasRule: true,
  },
  {
    date: "Aug 3, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: null,
    description: "USPS PO 2647800119",
    category: { emoji: "📦", name: "Shipping" },
    tag: null,
    amount: "−$9.85",
  },
  {
    date: "Aug 2, 2026",
    account: "BofA Credit Card",
    institution: "Bank of America",
    merchant: "Target",
    description: "TARGET T-2209",
    category: { emoji: "🧴", name: "Household" },
    tag: null,
    amount: "−$57.90",
    hasRule: true,
  },
];

// Widths track TransactionColumnSizer's flex ratios, but the fixed columns are
// tightened to what their content actually needs (date 92px, amount 86px mono,
// tag chip 83px) so the freed space flows to the four content columns. Tags
// moves to the widest breakpoint since only some rows carry one.
const COLUMN_DATE = "w-[7.5rem] grow-0 shrink-0";
const COLUMN_ACCOUNT =
  "w-36 grow-[0.4] shrink-[0.4] hidden xl:flex mock-desktop:flex";
const COLUMN_MERCHANT =
  "w-32 grow-[0.4] shrink-[0.4] hidden lg:flex mock-desktop:flex";
const COLUMN_DESCRIPTION = "w-36 grow-[0.6] shrink-[0.6]";
const COLUMN_CATEGORY = "w-36 grow-[0.4] shrink-[0.4]";
const COLUMN_TAGS = "w-24 grow-[0.2] shrink-[0.4] hidden 2xl:flex";
const COLUMN_RULE = "w-8 grow-0 shrink-0 hidden md:flex mock-desktop:flex";
const COLUMN_REVIEWED = "w-8 grow-0 shrink-0 hidden md:flex mock-desktop:flex";
// The real column fits its longest amount; this fits the mock's, "+$3,120.55".
const COLUMN_AMOUNT = "w-28 flex-none";

const LOGO_CIRCLE =
  "flex h-[18.5px] w-[18.5px] shrink-0 items-center justify-center rounded-full border border-surface-700";

// TransactionColumnSpacer: narrow, widening into spare room up to 20px.
function ColumnSpacer(props: { className?: string }): JSX.Element {
  return (
    <div
      className={buildClasses(
        "w-2",
        "max-w-5",
        "grow-[0.1]",
        "shrink-[4]",
        props.className ?? "",
      )}
    />
  );
}

function BrandGlyph(props: { hex: string; path: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={buildClasses("h-[11px]", "w-[11px]")}
      fill={props.hex}
      aria-hidden="true"
    >
      <path d={props.path} />
    </svg>
  );
}

// Mirrors MerchantLogo: a brand mark when one exists, otherwise the merchant's
// initial in a circle -- which is what the real component renders whenever Plaid
// has no logo for that merchant.
function MerchantLogo(props: { name: string }): JSX.Element {
  const mark = BRAND_MARKS[props.name];

  if (mark !== undefined) {
    return (
      <div className={buildClasses(LOGO_CIRCLE, "bg-white", "opacity-80")}>
        <BrandGlyph hex={mark.hex} path={mark.path} />
      </div>
    );
  }

  return (
    <div
      className={buildClasses(
        LOGO_CIRCLE,
        "opacity-80",
        "text-[0.75rem]",
        "text-on-surface-500",
      )}
    >
      {props.name.charAt(0)}
    </div>
  );
}

// Mirrors InstitutionLogo: the real component renders Plaid's raster logo in a
// bordered circle and, unlike MerchantLogo, does not dim it. These are the same
// 152px assets Plaid serves, bundled locally rather than hotlinked -- see the
// note in MerchantBrandMarks. Institutions without one get the filled
// account_balance glyph, exactly as the real component does.
const INSTITUTION_LOGOS: Record<string, string> = {
  "Bank of America": bofaLogo,
  Venmo: venmoLogo,
  Vanguard: vanguardLogo,
};

function InstitutionLogo(props: { name: string }): JSX.Element {
  const logo = INSTITUTION_LOGOS[props.name];

  if (logo !== undefined) {
    return (
      <img
        src={logo}
        alt=""
        className={buildClasses(
          "h-[18.5px]",
          "w-[18.5px]",
          "shrink-0",
          "rounded-full",
          "border",
          "border-surface-700",
        )}
      />
    );
  }

  return (
    <div className={LOGO_CIRCLE}>
      <RyeIcon
        name="account_balance"
        size="custom"
        fill
        className={buildClasses("text-[0.7rem]", "text-on-surface-500")}
      />
    </div>
  );
}

// RyeBadge size="sm" variant="outlined" vibe="ordinary" shape="fully-round".
function Badge(props: { children: string }): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "h-7",
        "shrink-0",
        "items-center",
        "rounded-full",
        "border",
        "border-on-surface-300",
        "bg-on-surface-100",
        "px-2.5",
        "text-sm",
        "text-on-surface-600",
      )}
    >
      {props.children}
    </div>
  );
}

function Cell(props: {
  className: string;
  children?: React.ReactNode;
  align?: "right";
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        props.className,
        "flex",
        "h-full",
        "items-center",
      )}
    >
      <div
        className={buildClasses(
          "flex",
          "h-full",
          "w-full",
          "items-center",
          "overflow-hidden",
          "px-3",
          {
            if: props.align === "right",
            then: "justify-end",
            else: "justify-start",
          },
        )}
      >
        {props.children}
      </div>
    </div>
  );
}

// RULE and REVIEWED don't use the padded cell: the real components centre their
// icon inside an h-8 w-8 circle. Centring matters because the amber dot and the
// check are different widths -- left-aligned, they don't share a centre.
function IconCell(props: {
  className: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        props.className,
        "flex",
        "h-full",
        "items-center",
      )}
    >
      <div
        className={buildClasses(
          "flex",
          "h-8",
          "w-8",
          "items-center",
          "justify-center",
          "rounded-full",
        )}
      >
        {props.children}
      </div>
    </div>
  );
}

function Row(props: { row: MockRow }): JSX.Element {
  const { row } = props;

  return (
    <div
      className={buildClasses(
        "flex",
        // The real app uses h-10; the mock runs one step tighter so more of the
        // table fits the 16:9 frame without feeling cramped.
        "h-9",
        "items-center",
        "justify-center",
        "bg-surface",
        "py-1",
        "border-t-tiny",
        "border-t-surface-600",
        "first:border-t-0",
      )}
    >
      <Cell className={COLUMN_DATE}>
        <span className={buildClasses("truncate")}>{row.date}</span>
      </Cell>
      <ColumnSpacer />
      <Cell className={COLUMN_ACCOUNT}>
        <InstitutionLogo name={row.institution} />
        <span className={buildClasses("truncate", "pl-3")}>{row.account}</span>
      </Cell>
      <ColumnSpacer className="hidden xl:block mock-desktop:block" />
      <Cell className={COLUMN_MERCHANT}>
        {row.merchant ? (
          <Fragment>
            <MerchantLogo name={row.merchant} />
            <span className={buildClasses("truncate", "pl-3")}>
              {row.merchant}
            </span>
          </Fragment>
        ) : null}
      </Cell>
      <ColumnSpacer className="hidden lg:block mock-desktop:block" />
      <Cell className={COLUMN_DESCRIPTION}>
        <span className={buildClasses("truncate")}>{row.description}</span>
      </Cell>
      <ColumnSpacer />
      <Cell className={COLUMN_CATEGORY}>
        {/* An uncategorized cell renders empty in the real app -- the "Select
            category" placeholder is invisible until hover or selection. */}
        {row.category ? (
          <Fragment>
            <span className={buildClasses("shrink-0")}>
              {row.category.emoji}
            </span>
            <span className={buildClasses("truncate", "pl-2")}>
              {row.category.name}
            </span>
          </Fragment>
        ) : null}
      </Cell>
      <ColumnSpacer />
      <Cell className={COLUMN_TAGS}>
        {row.tag ? (
          <span
            className={buildClasses(
              "truncate",
              "rounded",
              "bg-green-100",
              "px-1.5",
              "text-sm",
              "text-green-700",
            )}
          >
            {row.tag}
          </span>
        ) : null}
      </Cell>
      <ColumnSpacer className="hidden 2xl:block" />
      <IconCell className={COLUMN_RULE}>
        {row.hasRule ? (
          <RyeIcon
            name="gavel"
            size="sm"
            className={buildClasses("text-purple")}
          />
        ) : null}
      </IconCell>
      <ColumnSpacer className="hidden md:block mock-desktop:block" />
      <IconCell className={COLUMN_REVIEWED}>
        {row.reviewed !== false ? (
          <RyeIcon name="done" size="md" className="text-purple" />
        ) : (
          <RyeIcon
            name="circle"
            size="custom"
            fill
            className={buildClasses("text-yellow", "text-[0.7rem]")}
          />
        )}
      </IconCell>
      <ColumnSpacer className="hidden md:block mock-desktop:block" />
      <Cell className={COLUMN_AMOUNT} align="right">
        <span
          className={buildClasses("truncate", "font-mono", {
            if: row.positive === true,
            then: "text-green font-medium",
          })}
        >
          {row.amount}
        </span>
      </Cell>
    </div>
  );
}

// Phones can't fit the column layout legibly, so rows stack instead.
function MobileRow(props: { row: MockRow }): JSX.Element {
  const { row } = props;

  return (
    <div
      className={buildClasses(
        "border-t-tiny",
        "border-t-surface-600",
        "first:border-t-0",
        "bg-surface",
        "px-3",
        "py-2.5",
      )}
    >
      <div
        className={buildClasses(
          "flex",
          "items-baseline",
          "justify-between",
          "gap-3",
        )}
      >
        <span className={buildClasses("truncate")}>{row.description}</span>
        <span
          className={buildClasses("shrink-0", "font-mono", {
            if: row.positive === true,
            then: "text-green font-medium",
          })}
        >
          {row.amount}
        </span>
      </div>
      <div
        className={buildClasses(
          "mt-1",
          "flex",
          "items-center",
          "gap-2",
          "text-sm",
          "text-on-surface-500",
        )}
      >
        <span>{row.date}</span>
        {row.reviewed === false ? (
          <RyeIcon
            name="circle"
            size="custom"
            fill
            className={buildClasses("text-yellow", "text-[0.7rem]")}
          />
        ) : null}
        {row.category ? (
          <span className={buildClasses("truncate")}>
            {row.category.emoji} {row.category.name}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function TransactionsMock(): JSX.Element {
  return (
    <MockAppFrame activeTab="transactions">
      <MockPanel className="flex-col">
        {/* RyeMainContentHeader: the title, search, and actions, in one row. */}
        <div className={buildClasses("shrink-0", "grow-0", "px-[18px]")}>
          <div
            className={buildClasses("flex", "h-11", "items-center", "gap-6")}
          >
            <div
              className={buildClasses(
                "shrink-0",
                "text-base",
                "text-on-surface-700",
              )}
            >
              Transactions
            </div>
            <div
              className={buildClasses(
                "flex",
                "min-w-0",
                "flex-1",
                "items-center",
                "justify-between",
                "gap-3",
              )}
            >
              <div
                className={buildClasses(
                  "relative",
                  "flex",
                  "h-7",
                  "w-96",
                  "max-w-full",
                  "shrink",
                  "items-center",
                  "rounded-full",
                  "border",
                  "border-on-surface-200",
                  "pl-10",
                  "pr-12",
                  "text-sm",
                  "text-on-surface-400",
                )}
              >
                <RyeIcon
                  name="search"
                  size="sm"
                  className={buildClasses("absolute", "left-3")}
                />
                <span className={buildClasses("truncate")}>
                  Search transactions
                </span>
                {/* Exact-match toggle: RyeButton size xs, transparent/subdued. */}
                <div
                  className={buildClasses(
                    "absolute",
                    "right-2",
                    "flex",
                    "h-5",
                    "w-5",
                    "items-center",
                    "justify-center",
                    "rounded-md",
                    "text-on-surface-500",
                  )}
                >
                  <RyeIcon name="match_word" size="sm" />
                </div>
              </div>
              {/* RyeButton size="sm" variant="outlined" vibe="subdued" -- no fill. */}
              <div
                className={buildClasses(
                  "flex",
                  "h-7",
                  "shrink-0",
                  "items-center",
                  "gap-1.5",
                  "rounded-md",
                  "border",
                  "border-on-surface-200",
                  "px-2.5",
                  "text-sm",
                  "text-on-surface-500",
                )}
              >
                <RyeIcon
                  name="draft_orders"
                  size="sm"
                  className={buildClasses("text-yellow")}
                />
                <span
                  className={buildClasses(
                    "hidden",
                    "md:inline",
                    "mock-desktop:inline",
                  )}
                >
                  Review 3 transactions
                </span>
                <span
                  className={buildClasses("md:hidden", "mock-desktop:hidden")}
                >
                  3
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* TransactionsColumnHeaders: in the top bar, so the table scrolls
            beneath them. No line under them. */}
        <div
          className={buildClasses(
            "hidden",
            "sm:block",
            "mock-desktop:block",
            "shrink-0",
            "px-[18px]",
          )}
        >
          <div
            className={buildClasses(
              "-mx-2",
              "flex",
              "h-10",
              "items-center",
              "justify-center",
              "py-1",
              "font-medium",
            )}
          >
            <Cell className={COLUMN_DATE}>
              Date
              <div className={buildClasses("w-3")} />
              <RyeIcon
                name="arrow_circle_down"
                size="sm"
                fill
                className={buildClasses("text-purple")}
              />
            </Cell>
            <ColumnSpacer />
            <Cell className={COLUMN_ACCOUNT}>Account</Cell>
            <ColumnSpacer className="hidden xl:block mock-desktop:block" />
            <Cell className={COLUMN_MERCHANT}>Merchant</Cell>
            <ColumnSpacer className="hidden lg:block mock-desktop:block" />
            <Cell className={COLUMN_DESCRIPTION}>Description</Cell>
            <ColumnSpacer />
            <Cell className={COLUMN_CATEGORY}>Category</Cell>
            <ColumnSpacer />
            <Cell className={COLUMN_TAGS}>Tags</Cell>
            <ColumnSpacer className="hidden 2xl:block" />
            <IconCell className={COLUMN_RULE}>
              <RyeIcon name="gavel" size="sm" />
            </IconCell>
            <ColumnSpacer className="hidden md:block mock-desktop:block" />
            <IconCell className={COLUMN_REVIEWED}>
              <RyeIcon name="done" size="md" />
            </IconCell>
            <ColumnSpacer className="hidden md:block mock-desktop:block" />
            <Cell className={COLUMN_AMOUNT} align="right">
              Amount
            </Cell>
          </div>
        </div>

        {/* RyeMainContentCard */}
        <div
          className={buildClasses(
            "min-h-0",
            "flex-1",
            "overflow-hidden",
            "px-[18px]",
          )}
        >
          {/* TransactionsTable: pulled out by TRANSACTIONS_TABLE_INSET. */}
          <div className={buildClasses("-mx-2", "flex", "flex-col")}>
            {/* Mobile and desktop rows live in separate lists so that
                first:border-t-0 actually matches the first row of each. When
                they were interleaved, the first desktop row kept its top
                border. */}
            <div className={buildClasses("sm:hidden", "mock-desktop:hidden")}>
              {ROWS.map((row) => (
                <MobileRow key={`${row.date}-${row.description}`} row={row} />
              ))}
            </div>
            <div
              className={buildClasses(
                "hidden",
                "sm:block",
                "mock-desktop:block",
              )}
            >
              {ROWS.map((row) => (
                <Row key={`${row.date}-${row.description}`} row={row} />
              ))}
            </div>
          </div>
        </div>

        {/* RyeMainContentFooter */}
        <div className={buildClasses("shrink-0", "grow-0", "px-[18px]")}>
          <div
            className={buildClasses(
              "flex",
              "w-full",
              "items-center",
              "justify-end",
              "gap-2",
              "py-2",
            )}
          >
            <Badge>Count: 2,842</Badge>
            <Badge>Unreviewed: 3</Badge>
            <div
              className={buildClasses(
                "hidden",
                "lg:block",
                "mock-desktop:block",
              )}
            >
              <Badge>Sum: −$6,033.90</Badge>
            </div>
            <div
              className={buildClasses(
                "hidden",
                "xl:block",
                "mock-desktop:block",
              )}
            >
              <Badge>Average: −$2.12</Badge>
            </div>
          </div>
        </div>
      </MockPanel>
    </MockAppFrame>
  );
}
